import { useEffect, useState } from "react";
import { http, formatApiError } from "../../lib/api";
import { Route, CheckCircle2, Circle, Flag, Play } from "lucide-react";
import { CodeScanner } from "./CodeScanner";

const here = () => new Promise((res) => {
  if (!navigator.geolocation) return res({});
  navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lon: p.coords.longitude }), () => res({}), { enableHighAccuracy: true, timeout: 6000 });
});

function RunView({ run, onChange }) {
  const [msg, setMsg] = useState(null);
  const done = new Set(run.scans.map((s) => s.point_id));
  const scan = async (code, method) => {
    setMsg(null);
    try {
      const r = await http.post(`/agent-app/rounds/run/${run.id}/scan`, { code, method, ...(await here()) });
      setMsg({ ok: true, text: `« ${r.data.scan.name} » validé${r.data.instructions ? ` — ${r.data.instructions}` : ""}` });
      onChange({ ...run, scans: [...run.scans, r.data.scan] });
    } catch (e) { navigator.vibrate?.([60, 60, 60]); setMsg({ ok: false, text: formatApiError(e) }); }
  };
  const finish = async () => {
    const left = run.expected.length - done.size;
    if (left && !window.confirm(`${left} point(s) non validé(s). Terminer quand même ?`)) return;
    try { const r = await http.post(`/agent-app/rounds/run/${run.id}/finish`); onChange(null, r.data); }
    catch (e) { setMsg({ ok: false, text: formatApiError(e) }); }
  };
  return (
    <div className="space-y-3" data-testid="round-run">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#C9962E]">Ronde en cours</div>
        <div className="font-display text-lg font-extrabold text-[#0B2A7A]">{run.circuit_name}</div>
        <div className="text-xs text-slate-500">{run.site_name} · {done.size}/{run.expected.length} points{run.ordered ? " · dans l'ordre" : ""}</div>
      </div>
      <CodeScanner onScan={scan} />
      {msg && <div data-testid="round-scan-msg" className={`text-sm rounded-lg px-3 py-2 ${msg.ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-rose-50 text-rose-800 border border-rose-200"}`}>{msg.text}</div>}
      <ul className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="round-points-list">
        {run.expected.map((p) => {
          const s = run.scans.find((x) => x.point_id === p.id);
          return (
            <li key={p.id} className="px-4 py-3 flex items-center gap-3" data-testid={`round-point-${p.id}`}>
              {s ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5 text-slate-300" />}
              <span className="flex-1 text-sm font-semibold">{p.name}</span>
              {s && <span className="text-xs text-slate-500">{new Date(s.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>}
            </li>
          );
        })}
      </ul>
      <button data-testid="round-finish-btn" onClick={finish} className="w-full flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-500 text-white font-bold rounded-xl py-3 transition-colors"><Flag className="h-4 w-4" />Terminer la ronde</button>
    </div>
  );
}

export default function AgentRounds() {
  const [data, setData] = useState(null);
  const [last, setLast] = useState(null);
  const [err, setErr] = useState("");
  const load = () => http.get("/agent-app/rounds").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, []);
  const start = async (cid) => {
    setErr(""); setLast(null);
    try { const r = await http.post(`/agent-app/rounds/${cid}/start`); setData({ ...data, current: r.data }); }
    catch (e) { setErr(formatApiError(e)); }
  };
  if (!data) return <div className="text-sm text-slate-500">Chargement…</div>;
  if (!data.available) return <div data-testid="rounds-unavailable" className="bg-white border border-slate-200 rounded-xl p-5 text-center text-sm text-slate-500">Les rondes ne sont pas activées pour votre société.</div>;
  if (data.current) return <RunView run={data.current} onChange={(run, fin) => { setData({ ...data, current: run }); if (fin) setLast(fin); }} />;
  return (
    <section className="space-y-3" data-testid="agent-rounds">
      <h2 className="font-display text-lg font-extrabold text-[#0B2A7A]">Mes rondes</h2>
      {last && (
        <div data-testid="round-last-result" className={`rounded-xl px-4 py-3 text-sm border ${last.status === "completed" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
          {last.status === "completed" ? "Ronde terminée, tous les points sont validés." : `Ronde terminée avec ${last.missed.length} point(s) manquant(s) : ${last.missed.join(", ")}.`}
          {last.late && " Durée maximale dépassée."}
        </div>
      )}
      {err && <div data-testid="rounds-error" className="text-sm text-rose-700">{err}</div>}
      {data.circuits.length === 0 && <div className="bg-white border border-slate-200 rounded-xl p-5 text-center text-sm text-slate-500">Aucune ronde prévue sur vos sites aujourd&apos;hui.</div>}
      {data.circuits.map((c) => (
        <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
          <Route className="h-5 w-5 text-[#C9962E]" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm truncate">{c.name}</div>
            <div className="text-xs text-slate-500">{c.site_name} · {c.points.length} points{c.max_minutes ? ` · ${c.max_minutes} min max` : ""}</div>
          </div>
          <button data-testid={`round-start-${c.id}`} onClick={() => start(c.id)} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold rounded-lg px-3 py-2 transition-colors"><Play className="h-4 w-4" />Démarrer</button>
        </div>
      ))}
    </section>
  );
}
